/**
 * Main Distribution Hook
 *
 * This hook provides a clean interface for distribution operations
 * following clean architecture principles and single responsibility.
 *
 * @module useDistribution
 */

import {useCallback, useMemo} from 'react';
import {useDistributionStore} from '../store/distributionStore';
import {DistributionErrorHandler} from '../services/DistributionErrorHandler';
import {useDistributionData} from './useDistributionData';
import {useDistributionActions} from './useDistributionActions';
import {useDistributionFilters} from './useDistributionFilters';
import {DistributionFactory} from '../store/distributionFactory';

export const useDistribution = () => {
  const {
    distributionHistory,
    marketers,
    products,
    availableSerials,
    productAvailableSerials,
    isLoading,
    error,
    selectedRecord,
    selectedProductId,
    pagination,
    setSelectedRecord,
    setSelectedProductId,
    resetStore,
    setProductAvailableSerials,
    clearError
  } = useDistributionStore();

  // Use focused hooks for different responsibilities
  const {
    fetchMarketers,
    fetchProducts,
    fetchDistributionHistory,
    fetchAvailableSerialsForProduct,
    handlePageChange,
    handlePageSizeChange
  } = useDistributionData();

  const {
    createDelivery,
    deleteTransaction
  } = useDistributionActions();

  const {
    updateFilters,
    resetFilters
  } = useDistributionFilters();

  // Clear product available serials
  const clearProductAvailableSerials = useCallback(() => {
    setProductAvailableSerials([]);
  }, [setProductAvailableSerials]);

  // Business logic use cases
  const getPendingDeliveriesUseCase = useMemo(() =>
    DistributionFactory.createGetPendingDeliveriesUseCase(), []
  );
  const getCompletedDeliveriesUseCase = useMemo(() =>
    DistributionFactory.createGetCompletedDeliveriesUseCase(), []
  );

  // Computed values using use cases (memoized for performance)
  const computedPendingDeliveries = useMemo(() => {
    try {
      return getPendingDeliveriesUseCase.execute(distributionHistory);
    } catch (error) {
      return [];
    }
  }, [getPendingDeliveriesUseCase, distributionHistory]);

  const computedCompletedDeliveries = useMemo(() => {
    try {
      return getCompletedDeliveriesUseCase.execute(distributionHistory);
    } catch (error) {
      return [];
    }
  }, [getCompletedDeliveriesUseCase, distributionHistory]);

  // Additional computed values for compatibility
  const totalRecords = useMemo(() => pagination?.total || 0, [pagination?.total]);
  const pendingCount = useMemo(() => computedPendingDeliveries.length, [computedPendingDeliveries.length]);
  const completedCount = useMemo(() => computedCompletedDeliveries.length, [computedCompletedDeliveries.length]);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      await Promise.all([
        fetchMarketers(),
        fetchProducts(),
        fetchDistributionHistory() // Add distribution history to initial load
      ]);
    } catch (error) {
      DistributionErrorHandler.logError(error, 'loadInitialData');
    }
  }, [fetchMarketers, fetchProducts, fetchDistributionHistory]);

  return {
    // State
    distributionHistory,
    marketers,
    products,
    availableSerials,
    productAvailableSerials,
    isLoading,
    error,
    selectedRecord,
    selectedProductId,
    pagination,

    // Computed values
    pendingDeliveries: computedPendingDeliveries,
    completedDeliveries: computedCompletedDeliveries,
    totalRecords,
    pendingCount,
    completedCount,

    // Actions
    fetchMarketers,
    fetchProducts,
    fetchAvailableSerialsForProduct,
    handlePageChange,
    handlePageSizeChange,
    clearProductAvailableSerials,
    loadInitialData,
    createDelivery,
    deleteTransaction,
    updateFilters,
    setSelectedRecord,
    setSelectedProductId,
    resetFilters,
    resetStore,
    clearError
  };
};
